import React from "react";
import {
  Alert,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Link,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import LoginLayout from "../../layouts/LoginLayout";
import { useAuth } from "../../contexts/UserContext";
import { common } from "../../i18n";
import { EMAIL_REGEX } from "../../constants/form";
import { InputReactHookForm } from "../../components/input/InputReactHookForm";
import { LOGO_IMG_SRC } from "../../constants";

interface LoginFormValues {
  email: string;
  password: string;
  rememberMe: boolean;
}

const formatCooldown = (seconds: number): string => {
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h`;
  if (seconds >= 60) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  return `${seconds}s`;
};

const LoginPage: React.FC = () => {
  const { login, loginCooldownSeconds } = useAuth();
  const [searchParams] = useSearchParams();

  // โชว์ context banner เมื่อ user มาจาก /auth/link bounce (returnTo จะชี้กลับไปที่นั่น).
  // ทำให้ context ของ flow เชื่อมบัญชีไม่หายไปบนหน้า login — match กับ
  // top-tier OAuth pattern (Google/GitHub: app name โผล่ทุกหน้า รวมถึง login)
  const returnTo = searchParams.get("returnTo");
  const isLinkingFlow = returnTo?.startsWith("/auth/link") ?? false;

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = useForm<LoginFormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      email: "",
      password: "",
      rememberMe: true,
    },
    delayError: 400,
  });

  const onSubmit = async (values: LoginFormValues) => {
    await login(values.email, values.password, values.rememberMe);
  };

  const { t } = useTranslation([common]);

  return (
    <LoginLayout>
      <Box
        maxWidth={"400px"}
        display={"flex"}
        flexDirection={"column"}
        rowGap={"20px"}
        width={"100%"}
      >
        <img
          src={LOGO_IMG_SRC}
          alt="logo"
          style={{
            padding: "12px 0px",
            height: "28px",
            width: "233px",
            marginBottom: "20px",
          }}
        />

        <Typography variant="h1">{t("Login")}</Typography>

        {isLinkingFlow && (
          <Alert severity="info" variant="outlined">
            {t("Link-Signin-Banner")}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "100%",
            }}
          >
            <InputReactHookForm
              required
              control={control}
              name={"email"}
              label={t("Email")}
              placeholder={t("Email")}
              rules={{
                required: t("form-reqired-error", {
                  param: t("Email"),
                }),
                pattern: {
                  value: EMAIL_REGEX,
                  message: t("Must-be-a-valid-email"),
                },
              }}
            />

            <InputReactHookForm
              required
              rules={{
                required: t("form-reqired-error", {
                  param: t("Password"),
                }),
              }}
              control={control}
              name={"password"}
              label={t("Password")}
              placeholder={t("Password")}
              type="password"
            />

            <Box
              display={"flex"}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Controller
                name={"rememberMe"}
                control={control}
                defaultValue={true}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label={t("Remember-Me")}
                    sx={{
                      margin: "0px 0px 0px -9px",
                    }}
                  />
                )}
              />

              {/* Disabled until reset-password API is ready */}

              <Link
                href="/forgot-password"
                underline="hover"
                color={"var(--dark)"}
                variant="text1"
              >
                {t("Forgot-Password")}
              </Link>
            </Box>
          </Box>

          <Box marginTop={"40px"}>
            <Button
              type="submit"
              variant="contained"
              color={"secondary"}
              size="large"
              fullWidth
              disabled={!isValid || isSubmitting || loginCooldownSeconds > 0}
            >
              {loginCooldownSeconds > 0
                ? `${t("Login")} (${formatCooldown(loginCooldownSeconds)})`
                : t("Login")}
            </Button>
          </Box>
        </form>
      </Box>
    </LoginLayout>
  );
};

export default LoginPage;
